import { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Popconfirm, Space, Table, Tag, message } from 'antd';
import { ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';

import {
  createFeaturedCollection,
  deleteFeaturedCollection,
  listFeaturedCollections,
  reorderFeaturedCollections,
  updateFeaturedCollectionImage,
  uploadFeaturedImage,
  type FeaturedCollectionAdmin,
} from '../../services/admin';
import { apiErrorMessage } from './desafios';
import { moveItem } from './move-item';
import { EdicaoSelect } from './catalog/EdicaoSelect';
import { accessTag } from './catalog/edicao-access';
import { ACCEPTED_MIME, checkImage } from './Editor/modules/rebus/upload';

interface DestaqueCatalogProps {
  /** Quando a aba fica ativa a lista é relida. */
  active?: boolean;
}

const IMAGE_TOO_LARGE = 'Imagem muito grande (máximo 5 MB).';

/**
 * Mensagem do erro de upload/gravação. Espelha o mapeamento do rébus
 * (Editor/modules/rebus/upload.ts): o Nest devolve 413 em inglês ("File too large")
 * para arquivo acima do teto; 400 traz a mensagem da API.
 */
const featuredErrorMessage = (err: unknown, fallback: string): string => {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 413) return IMAGE_TOO_LARGE;
  return apiErrorMessage(err) ?? fallback;
};

const collectionName = (f: FeaturedCollectionAdmin): string =>
  f.edicao.nome ?? `Edição ${f.edicao.id}`;

export const DestaqueCatalog: React.FC<DestaqueCatalogProps> = ({ active = true }) => {
  const [items, setItems] = useState<FeaturedCollectionAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [reordering, setReordering] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [edicaoId, setEdicaoId] = useState<number | undefined>();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await listFeaturedCollections());
    } catch {
      message.error('Falha ao carregar os destaques.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (active) load();
  }, [active, load]);

  useEffect(() => {
    if (!file) {
      setPreview(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const openCreate = () => {
    setEdicaoId(undefined);
    setFile(null);
    setModalOpen(true);
  };

  const pickFile = (picked: File | undefined, onValid: (f: File) => void) => {
    if (!picked) return;
    const problem = checkImage(picked);
    if (problem) {
      message.error(problem);
      return;
    }
    onValid(picked);
  };

  const handleCreate = async () => {
    if (edicaoId === undefined || !file) return;
    setSaving(true);
    try {
      const { url } = await uploadFeaturedImage(file);
      await createFeaturedCollection(edicaoId, url);
      message.success('Destaque adicionado.');
      setModalOpen(false);
      await load();
    } catch (err: unknown) {
      message.error(featuredErrorMessage(err, 'Falha ao adicionar o destaque.'));
    } finally {
      setSaving(false);
    }
  };

  const handleReplaceImage = async (f: FeaturedCollectionAdmin, picked: File) => {
    setBusyId(f.id);
    try {
      const { url } = await uploadFeaturedImage(picked);
      await updateFeaturedCollectionImage(f.id, url);
      message.success('Foto atualizada.');
      await load();
    } catch (err: unknown) {
      message.error(featuredErrorMessage(err, 'Falha ao trocar a foto.'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (f: FeaturedCollectionAdmin) => {
    try {
      await deleteFeaturedCollection(f.id);
      message.success('Destaque removido.');
      await load();
    } catch (err: unknown) {
      message.error(apiErrorMessage(err) ?? 'Falha ao remover o destaque.');
    }
  };

  const handleMove = async (index: number, delta: -1 | 1) => {
    const next = moveItem(items, index, delta);
    if (next === items) return;
    setItems(next);
    setReordering(true);
    try {
      await reorderFeaturedCollections(next.map((f) => f.id));
      message.success('Ordem salva.');
    } catch {
      message.error('Falha ao reordenar.');
      await load();
    } finally {
      setReordering(false);
    }
  };

  const columns = [
    {
      title: 'Ordem',
      key: 'ordem',
      width: 96,
      render: (_: unknown, f: FeaturedCollectionAdmin, index: number) => (
        <Space size={4}>
          <Button
            size="small"
            aria-label={`Subir ${collectionName(f)}`}
            icon={<ArrowUpOutlined />}
            disabled={index === 0 || reordering}
            onClick={() => handleMove(index, -1)}
          />
          <Button
            size="small"
            aria-label={`Descer ${collectionName(f)}`}
            icon={<ArrowDownOutlined />}
            disabled={index === items.length - 1 || reordering}
            onClick={() => handleMove(index, 1)}
          />
        </Space>
      ),
    },
    {
      title: 'Foto',
      key: 'foto',
      width: 84,
      render: (_: unknown, f: FeaturedCollectionAdmin) => (
        <img
          src={f.imageUrl}
          alt=""
          style={{ width: 60, height: 80, objectFit: 'cover', borderRadius: 4, display: 'block' }}
        />
      ),
    },
    {
      title: 'Coleção',
      key: 'nome',
      render: (_: unknown, f: FeaturedCollectionAdmin) => collectionName(f),
    },
    {
      title: 'Acesso',
      key: 'acesso',
      width: 260,
      render: (_: unknown, f: FeaturedCollectionAdmin) => {
        const tag = accessTag(f.edicao.free, f.edicao.price);
        return (
          <Space size={4}>
            <Tag color={tag.color}>{tag.label}</Tag>
            {!f.edicao.eligible && <Tag color="red">Sem jogos</Tag>}
          </Space>
        );
      },
    },
    {
      title: 'Ações',
      key: 'acoes',
      width: 240,
      render: (_: unknown, f: FeaturedCollectionAdmin) => (
        <Space>
          <Button size="small" disabled={busyId === f.id} style={{ position: 'relative' }}>
            Trocar foto
            <input
              type="file"
              accept={ACCEPTED_MIME.join(',')}
              aria-label={`Nova foto de ${collectionName(f)}`}
              style={{ position: 'absolute', inset: 0, opacity: 0, width: '100%', cursor: 'pointer' }}
              onChange={(e) => {
                const picked = e.target.files?.[0];
                e.target.value = '';
                pickFile(picked, (valid) => handleReplaceImage(f, valid));
              }}
            />
          </Button>
          <Popconfirm
            title="Remover esta coleção do destaque?"
            okText="Remover"
            okButtonProps={{ danger: true }}
            cancelText="Cancelar"
            onConfirm={() => handleDelete(f)}
          >
            <Button size="small" danger>
              Remover
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Destaque</h2>
        <Button type="primary" onClick={openCreate}>
          + Adicionar destaque
        </Button>
      </div>

      <Table
        rowKey="id"
        loading={loading}
        columns={columns}
        dataSource={items}
        pagination={false}
        scroll={items.length ? { x: 760 } : undefined}
        locale={{
          emptyText: "Nenhuma coleção em destaque. O app mostra 'Nenhum destaque no momento'.",
        }}
      />

      <Modal
        title="Adicionar destaque"
        visible={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleCreate}
        okText="Salvar"
        cancelText="Cancelar"
        okButtonProps={{ disabled: edicaoId === undefined || !file }}
        confirmLoading={saving}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <EdicaoSelect
            value={edicaoId}
            onChange={(id) => setEdicaoId(id)}
            placeholder="Coleção"
            style={{ width: '100%' }}
            filter={(e) => e.kind !== 'MIND_TRAINING'}
            showAccess
          />
          <input
            type="file"
            accept={ACCEPTED_MIME.join(',')}
            aria-label="Foto do destaque"
            onChange={(e) => pickFile(e.target.files?.[0], setFile)}
          />
          <small>JPG, PNG ou WEBP até 5 MB, retrato 3:4</small>
          {preview && (
            <img
              src={preview}
              alt="Prévia da foto"
              style={{ width: 120, height: 160, objectFit: 'cover', borderRadius: 4 }}
            />
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default DestaqueCatalog;
